import { cn } from '@/lib/tailwind-utils.js';
import { ReactNode, useLayoutEffect, useRef } from 'react';

/**
 * Dims and freezes the assessment panel behind one centered confirmation.
 * The panel is `inert`, so a click or keypress behind the dialog is dropped.
 */
export function InAppAssessmentConfirmationFrame({
  locked,
  confirmation,
  children,
}: {
  locked: boolean;
  confirmation: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <InertRegion locked={locked} className="flex min-h-0 flex-1 flex-col">
        {children}
      </InertRegion>
      {locked && <div className="absolute inset-0 z-10 bg-black/40" aria-hidden />}
      {confirmation}
    </div>
  );
}

function InertRegion({ locked, className, children }: { locked: boolean; className?: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    node.inert = locked;
    if (locked) {
      node.setAttribute('inert', '');
    } else {
      node.removeAttribute('inert');
    }
  }, [locked]);

  return (
    <div ref={ref} className={cn(className, locked && 'pointer-events-none')}>
      {children}
    </div>
  );
}

/** Centers a confirmation over the dimmed panel and moves focus to its first field or action. */
export function InAppAssessmentCenteredConfirm({
  titleId,
  testId,
  children,
}: {
  titleId: string;
  testId: string;
  children: ReactNode;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }
    const target = dialog.querySelector<HTMLElement>('input:not([disabled]), button:not([disabled])');
    target?.focus();
  }, []);

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center p-4">
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid={testId}
        className="w-full max-w-[22rem] outline-none"
      >
        {children}
      </div>
    </div>
  );
}
